// use-filters.ts
// This file provides the useFilters hook for fetching available filters for the order table, integrating with user and department context.

import { useQuery } from "@tanstack/react-query";
import { Filter } from "@/types/order-table";
import { useAppState } from "@/contexts/app-state";
import { useOrdersContext } from "@contexts/orders-context";
import { getFilters } from "@/services/filters";

/**
 * useFilters Hook
 * Fetches available filters for the order table, based on user, department, and current filters context.
 *
 * @param type - The type of filter to get
 * @param currentTable - (Optional) The current table context
 * @returns The query object containing data, status, and helpers from React Query
 *
 * @example
 * const { data, isLoading } = useFilters("insurance");
 */
export const useFilters = (type: Filter, currentTable?: string) => {
  const { loggedInUser, context } = useAppState();
  const { filters } = useOrdersContext();

  return useQuery<string[], Error>({
    queryKey: ["filters", { user: loggedInUser, type, filters }],
    queryFn: () =>
      getFilters({
        user: loggedInUser,
        type,
        departmentId: context?.athena?.departmentId || "ABM-123",
        practiceId: context?.athena?.practiceId || "8363",
        filters,
        currentTable,
      }),
  });
};
